import { find } from 'lodash';
import { OptionClassificationIdentifiers } from '../Enums';

export type OptionClassification = {
  identifier: string;
  title?: string;
  selectedoptions?: Array<{
    identifier: string;
    amount?: number;
    title?: string;
    detailImage?: string | null;
    attributes?: unknown[];
  }>;
  hiddenInFrontend?: boolean;
};

/** Normalize optionclassifications whether the API returns an array or object keyed by identifier. */
export function normalizeOptionClassifications(optionclassifications: unknown): OptionClassification[] {
  if (!optionclassifications) return [];
  if (Array.isArray(optionclassifications)) return optionclassifications;
  if (typeof optionclassifications === 'object') {
    return Object.values(optionclassifications as Record<string, OptionClassification>);
  }
  return [];
}

export function getOptionClassification(
  optionclassifications: unknown,
  identifier: string
): OptionClassification | null {
  if (!optionclassifications || !identifier) return null;

  if (Array.isArray(optionclassifications)) {
    return find(optionclassifications, { identifier }) || null;
  }

  if (typeof optionclassifications === 'object') {
    const keyed = optionclassifications as Record<string, OptionClassification>;
    return keyed[identifier] || find(keyed, { identifier }) || null;
  }

  return null;
}

export function getSelectedOptionFromClassification(
  optionclassifications: unknown,
  identifier: string
) {
  const oc = getOptionClassification(optionclassifications, identifier);
  return oc?.selectedoptions?.[0] || null;
}

/** Number of colors selected in the configuration (used for designdata.colorAmount). */
export function getSelectedColorCount(optionclassifications: unknown): number {
  const oc = getOptionClassification(optionclassifications, OptionClassificationIdentifiers.color);
  const selected = oc?.selectedoptions ?? [];
  if (selected.length === 0) return 1;

  const hasAmount = selected.some((option) => typeof option.amount === 'number');
  if (hasAmount) {
    return Math.max(1, selected.filter((option) => (option.amount ?? 0) > 0).length);
  }

  return selected.length;
}
